import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../../lib/supabaseClient'
import toast from 'react-hot-toast'
import {
  HiOutlineSearch,
  HiOutlineBan,
  HiOutlineCheckCircle,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineFilter,
} from 'react-icons/hi'
import ConfirmModal from '../../components/ConfirmModal'
import useDocumentTitle from '../../hooks/useDocumentTitle'

const PAGE_SIZE = 10

export default function UsersPage() {
  useDocumentTitle('Users')

  const [users, setUsers] = useState([])
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [page, setPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [confirmModal, setConfirmModal] = useState({ open: false })

  // Fetch roles for filter dropdown
  useEffect(() => {
    async function fetchRoles() {
      const { data } = await supabase
        .from('roles')
        .select('id, description')
        .in('id', ['admin', 'client'])
        .order('id')
      setRoles(data || [])
    }
    fetchRoles()
  }, [])

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    try {
      let query = supabase
        .from('profiles')
        .select('*, roles(id, description), agencies(name)', { count: 'exact' })
        .in('role_id', ['admin', 'client'])
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)

      if (search.trim()) {
        query = query.or(
          `first_name.ilike.%${search.trim()}%,last_name.ilike.%${search.trim()}%,email.ilike.%${search.trim()}%`
        )
      }

      if (roleFilter !== 'all') {
        query = query.eq('role_id', roleFilter)
      }

      const { data, error, count } = await query

      if (error) throw error
      setUsers(data || [])
      setTotalCount(count || 0)
    } catch (err) {
      toast.error(err.message || 'Failed to load users')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [page, search, roleFilter])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  useEffect(() => {
    setPage(0)
  }, [search, roleFilter])

  const totalPages = Math.ceil(totalCount / PAGE_SIZE)

  const handleToggleDeleted = (user) => {
    const displayName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email
    setConfirmModal({
      open: true,
      title: user.is_deleted ? 'Restore User' : 'Deactivate User',
      message: `Are you sure you want to ${
        user.is_deleted ? 'restore' : 'deactivate'
      } "${displayName}"?${
        !user.is_deleted
          ? ' This user will no longer be able to access the platform.'
          : ''
      }`,
      variant: user.is_deleted ? 'success' : 'danger',
      onConfirm: async () => {
        try {
          const { error } = await supabase
            .from('profiles')
            .update({ is_deleted: !user.is_deleted })
            .eq('id', user.id)

          if (error) throw error
          toast.success(
            `User ${user.is_deleted ? 'restored' : 'deactivated'} successfully`
          )
          fetchUsers()
        } catch (err) {
          toast.error('Operation failed')
        }
        setConfirmModal({ open: false })
      },
    })
  }

  const getRoleBadgeClass = (roleName) => {
    switch (roleName) {
      case 'super_admin':
        return 'badge-purple'
      case 'admin':
        return 'badge-primary'
      case 'agent':
        return 'badge-info'
      case 'client':
        return 'badge-warning'
      default:
        return 'badge-default'
    }
  }

  return (
    <div className="users-page">
      <div className="page-header">
        <div>
          <h1>Users</h1>
          <p>Manage platform users, agents, and clients</p>
        </div>
      </div>

      {/* Search & Filter bar */}
      <div className="search-bar">
        <div className="search-input-wrapper">
          <HiOutlineSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <HiOutlineFilter className="filter-icon" />
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Roles</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.description || role.id}
              </option>
            ))}
          </select>
        </div>

        <span className="search-count">
          {totalCount} user{totalCount === 1 ? '' : 's'}
        </span>
      </div>

      {/* Table */}
      <div className="table-container">
        {loading ? (
          <div className="table-loading">
            <div className="loading-spinner" />
            <p>Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="table-empty">
            <HiOutlineSearch style={{ fontSize: '2rem', opacity: 0.4 }} />
            <p>No users found</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Agency</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const displayName = `${u.first_name || ''} ${u.last_name || ''}`.trim()
                const roleName = u.role_id || u.roles?.id || 'N/A'
                const roleLabel = u.roles?.description || roleName
                return (
                  <tr key={u.id} className={u.is_deleted ? 'row-banned' : ''}>
                    <td data-label="" className="td-user">
                      <div className="user-cell">
                        <div className="user-avatar-small">
                          {u.first_name?.charAt(0) || u.email?.charAt(0) || '?'}
                        </div>
                        <div className="cell-primary">
                          <strong>{displayName || 'No name'}</strong>
                          <span className="cell-secondary">{u.email}</span>
                        </div>
                      </div>
                    </td>
                    <td data-label="Role">
                      <span className={`badge ${getRoleBadgeClass(roleName)}`}>
                        {roleLabel}
                      </span>
                    </td>
                    <td data-label="Agency">{u.agencies?.name || '–'}</td>
                    <td data-label="Status">
                      {u.is_deleted ? (
                        <span className="badge badge-danger">Deactivated</span>
                      ) : (
                        <span className="badge badge-success">Active</span>
                      )}
                    </td>
                    <td data-label="" className="td-actions">
                      <button
                        className={`btn btn-sm ${
                          u.is_deleted ? 'btn-success' : 'btn-danger'
                        }`}
                        onClick={() => handleToggleDeleted(u)}
                      >
                        {u.is_deleted ? (
                          <>
                            <HiOutlineCheckCircle /> Restore
                          </>
                        ) : (
                          <>
                            <HiOutlineBan /> Deactivate
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-ghost"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            <HiOutlineChevronLeft /> Previous
          </button>
          <span className="pagination-info">
            Page {page + 1} of {totalPages}
          </span>
          <button
            className="btn btn-ghost"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next <HiOutlineChevronRight />
          </button>
        </div>
      )}

      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ open: false })}
      />
    </div>
  )
}
